import { ArrowUpRight, Sparkles } from "lucide-react";
import { ConversationDetails } from "@/types/conversation/conversation";
import { buildTopicTrends } from "../utils/aggregates";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface InsightsTopicTrendsProps {
  conversations: ConversationDetails[];
  dateRange: string;
  onAskAbout: (topic: string) => void;
}

export const InsightsTopicTrends = ({
  conversations,
  dateRange,
  onAskAbout,
}: InsightsTopicTrendsProps) => {
  const trends = buildTopicTrends(conversations, dateRange);
  const maxCount = trends.reduce((m, t) => Math.max(m, t.count), 0);
  // Bars only earn their place when there's a real distribution to compare.
  // If every topic appears in just one call, the chart is a row of identical
  // full-width bars — a plain list communicates the same thing more honestly.
  const showBars = maxCount >= 2;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden h-full">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
        <GenieIcon
          name="trending-up"
          size={24}
          color="#4E9CFF"
          secondaryColor="#FF4DB8"
          gradientEnabled
          label="Topic trends"
        />
        <div>
          <h3 className="text-base font-semibold text-gray-900">
            {showBars ? "Topic trends" : "Topics mentioned"}
          </h3>
          <p className="text-xs text-gray-500">
            {showBars
              ? "What customers keep bringing up"
              : "Pulled from recent calls — trends will appear once topics repeat"}
          </p>
        </div>
      </div>

      <div className="p-5">
        {trends.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">
              No topics extracted yet. Run analysis on a few conversations to see trends.
            </p>
          </div>
        ) : showBars ? (
          <div className="space-y-2.5">
            {trends.map((t) => {
              const widthPct = Math.max(8, (t.count / maxCount) * 100);
              return (
                <button
                  key={t.topic}
                  type="button"
                  onClick={() => onAskAbout(t.topic)}
                  className="w-full text-left group"
                >
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium text-gray-900 truncate group-hover:text-[#4E9CFF] transition-colors">
                        {t.topic}
                      </span>
                      {t.isNew && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#FFBB33]/15 text-[#B8860B]">
                          <Sparkles className="w-2.5 h-2.5" />
                          new
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-gray-600 tabular-nums">
                        {t.count} call{t.count === 1 ? "" : "s"}
                      </span>
                      {!t.isNew && t.delta !== 0 && (
                        <span
                          className={`text-[10px] font-semibold ${
                            t.delta > 0 ? "text-[#4E9CFF]" : "text-gray-400"
                          }`}
                        >
                          {t.delta > 0 ? "+" : ""}
                          {t.delta}
                        </span>
                      )}
                      <ArrowUpRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-[#4E9CFF] transition-colors" />
                    </div>
                  </div>
                  <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] group-hover:opacity-90 transition-opacity"
                      style={{ width: `${widthPct}%` }}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {trends.map((t) => (
              <li key={t.topic}>
                <button
                  type="button"
                  onClick={() => onAskAbout(t.topic)}
                  className="w-full text-left py-2 flex items-center justify-between gap-3 group"
                >
                  <span className="text-sm font-medium text-gray-900 truncate group-hover:text-[#4E9CFF] transition-colors">
                    {t.topic}
                  </span>
                  <ArrowUpRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-[#4E9CFF] transition-colors flex-shrink-0" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
